import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trainingConfig } from '../../config/trainingConfig';
import { TrainingCertification } from '../../redux/API/ClientAPIHelpers/trainingCertification';
import styles from './Training.module.scss';

interface QuizAnswer {
	questionId: number;
	selectedIndex: number;
}

const Training = () => {
	const navigate = useNavigate();
	const [currentModule, setCurrentModule] = useState<number | null>(null);
	const [completedModules, setCompletedModules] = useState<number[]>([]);
	const [showQuiz, setShowQuiz] = useState(false);
	const [quizAnswers, setQuizAnswers] = useState<QuizAnswer[]>([]);
	const [quizSubmitted, setQuizSubmitted] = useState(false);
	const [quizScore, setQuizScore] = useState(0);
	const [hasPassed, setHasPassed] = useState(false);
	const [submitError, setSubmitError] = useState('');

	const isModuleCompleted = (moduleId: number) => completedModules.includes(moduleId);

	const handleStartModule = (moduleId: number) => {
		setCurrentModule(moduleId);
	};

	const handleCompleteModule = (moduleId: number) => {
		setCompletedModules((prev) => {
			if (!prev.includes(moduleId)) {
				return [...prev, moduleId];
			}
			return prev;
		});
		if (moduleId < trainingConfig.modules.length) {
			handleStartModule(moduleId + 1);
		} else {
			setCurrentModule(null);
			setShowQuiz(true);
		}
	};

	const handleBackToModule = (moduleId: number) => {
		setCurrentModule(moduleId);
	};

	const handleQuizAnswer = (questionId: number, selectedIndex: number) => {
		setQuizAnswers((prev) => {
			const newAnswers = prev.filter((a) => a.questionId !== questionId);
			newAnswers.push({ questionId, selectedIndex });
			return newAnswers;
		});
	};

	const handleSubmitQuiz = async () => {
		// Calculate score
		let score = 0;
		trainingConfig.quiz.forEach((q) => {
			const answer = quizAnswers.find((a) => a.questionId === q.id);
			if (answer && answer.selectedIndex === q.correctIndex) {
				score++;
			}
		});

		const passed = score >= trainingConfig.passThreshold;

		setSubmitError('');

		try {
			const response = await TrainingCertification.submitTrainingResult({
				trainingId: trainingConfig.id,
				score,
				totalQuestions: trainingConfig.quiz.length,
				passed,
				answersJson: JSON.stringify(quizAnswers),
			});

			if (response?.error) {
				setSubmitError('Unable to save your training result. Please try again.');
				return;
			}
		} catch {
			setSubmitError('Unable to save your training result. Please try again.');
			return;
		}

		setQuizScore(score);
		setHasPassed(passed);
		setQuizSubmitted(true);

		if (passed) {
			setCompletedModules((prev) => {
				if (!prev.includes(-1)) {
					return [...prev, -1];
				}
				return prev;
			});
		}
	};

	const handleRetakeQuiz = () => {
		setQuizAnswers([]);
		setQuizSubmitted(false);
		setQuizScore(0);
		setHasPassed(false);
		setSubmitError('');
	};

	const handleReviewModules = () => {
		setShowQuiz(false);
		setQuizSubmitted(false);
		setSubmitError('');
		setCurrentModule(1);
	};

	const renderSection = (section: any, index: number) => {
		switch (section.type) {
			case 'heading':
				return (
					<h3 key={index} className={styles.sectionHeading}>
						{section.text}
					</h3>
				);
			case 'paragraph':
				return (
					<p key={index} className={styles.sectionText} dangerouslySetInnerHTML={{ __html: section.text }} />
				);
			case 'list':
				return (
					<ul key={index} className={section.ordered ? styles.sectionOrderedList : styles.sectionList}>
						{section.items?.map((item: string, i: number) => (
							<li key={i} className={styles.sectionListItem} dangerouslySetInnerHTML={{ __html: item }} />
						))}
					</ul>
				);
			case 'callout':
				return (
					<div key={index} className={styles.callout} dangerouslySetInnerHTML={{ __html: section.text }} />
				);
			default:
				return null;
		}
	};

	const renderBeacon = () => {
		return (
			<div className={styles.beacon}>
				{trainingConfig.modules.map((m, i) => (
					<Fragment key={m.id}>
						<div className={`${styles.lamp} ${isModuleCompleted(m.id) ? styles.lit : ''} ${currentModule === m.id ? styles.current : ''}`}>
							{i + 1}
						</div>
						{i < trainingConfig.modules.length - 1 && <div className={styles.beamLine} />}
					</Fragment>
				))}
				<div key='lamp-quiz' className={`${styles.lamp} ${styles.quiz} ${isModuleCompleted(-1) ? styles.lit : ''}`}>
					✓
				</div>
			</div>
		);
	};

	return (
		<div className={styles.trainingPage}>
			<header className={styles.header}>
				<div className={styles.headerInner}>
					<button className={styles.backToDashboard} onClick={() => navigate('/dashboard')}>
						← Back to dashboard
					</button>
					<div className={styles.eyebrow}>The Lighthouse · Behavior Technician Onboarding</div>
					<h1 className={styles.title}>{trainingConfig.title}</h1>
					<p className={styles.description}>{trainingConfig.description}</p>
					{renderBeacon()}
					<div className={styles.beaconLabel}>
						{isModuleCompleted(-1)
							? 'Training complete — certificate earned'
							: `Module ${completedModules.length} of ${trainingConfig.modules.length} complete`}
					</div>
				</div>
			</header>

			<div className={styles.wrap}>
			{currentModule === null && !showQuiz && completedModules.length === 0 && (
				<div className={styles.card}>
					<span className={styles.moduleTag}>Before you begin</span>
					<h2 className={styles.cardTitle}>Welcome to Illuminate</h2>
					<p className={styles.sectionText}>
						Illuminate is the system The Lighthouse uses to document client sessions. The information you enter serves three purposes:
					</p>
					<ul className={styles.sectionList}>
						<li className={styles.sectionListItem}>
							<strong>It tracks your client's ABA progress</strong> — your BCBA relies on your data.
						</li>
						<li className={styles.sectionListItem}>
							<strong>It tracks your hours for payroll</strong> — incomplete notes can delay payment.
						</li>
						<li className={styles.sectionListItem}>
							<strong>It supports your client's treatment</strong> — accurate data helps the BCBA make the right clinical
							decisions, so quality documentation directly benefits your client.
						</li>
					</ul>
					<p className={styles.sectionText}>
						If anything goes wrong with your Illuminate input, reach out to the support team right away.
					</p>
					<button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => handleStartModule(1)}>
						Start training
					</button>
				</div>
			)}

			{currentModule !== null && (
				<div className={styles.card}>
					<span className={styles.moduleTag}>{trainingConfig.modules[currentModule - 1].tag}</span>
					<h2 className={styles.cardTitle}>{trainingConfig.modules[currentModule - 1].title}</h2>
					{trainingConfig.modules[currentModule - 1].sections.map(renderSection)}
					<div className={styles.navRow}>
						{currentModule > 1 && (
							<button className={`${styles.btn} ${styles.btnGhost}`} onClick={() => handleBackToModule(currentModule - 1)}>
								Back
							</button>
						)}
						<button
							className={`${styles.btn} ${currentModule === trainingConfig.modules.length ? styles.btnAmber : styles.btnPrimary}`}
							onClick={() => handleCompleteModule(currentModule)}
						>
							{currentModule === trainingConfig.modules.length ? 'Finish modules → take the quiz' : 'Mark complete & continue'}
						</button>
					</div>
				</div>
			)}

			{showQuiz && !quizSubmitted && (
				<div className={styles.card}>
					<span className={styles.moduleTag}>Final step</span>
					<h2 className={styles.cardTitle}>Certification quiz</h2>
					<p className={styles.sectionText}>
						Answer all {trainingConfig.quiz.length} questions. You need <strong>{trainingConfig.passThreshold} out of {trainingConfig.quiz.length}</strong> to pass and receive your
						certificate. You can retake the quiz if needed.
					</p>
					{trainingConfig.quiz.map((q, qi) => {
						const answer = quizAnswers.find((a) => a.questionId === q.id);
						return (
							<div key={q.id} className={styles.qBlock}>
								<div className={styles.qNum}>Question {qi + 1} of {trainingConfig.quiz.length}</div>
								<div className={styles.qText}>{q.question}</div>
								{q.options.map((opt, oi) => (
									<label
										key={oi}
										className={`${styles.opt} ${answer?.selectedIndex === oi ? styles.picked : ''}`}
										onClick={() => handleQuizAnswer(q.id, oi)}
									>
										<input type='radio' name={`q${qi}`} checked={answer?.selectedIndex === oi} readOnly />
										<span>{opt}</span>
									</label>
								))}
							</div>
						);
					})}
					{submitError && <p className={`${styles.qFeedback} ${styles.no}`}>{submitError}</p>}
					<div className={styles.navRow}>
						<button className={`${styles.btn} ${styles.btnGhost}`} onClick={handleReviewModules}>
							Review modules
						</button>
						<button
							className={`${styles.btn} ${styles.btnAmber}`}
							onClick={handleSubmitQuiz}
							disabled={quizAnswers.length !== trainingConfig.quiz.length}
						>
							Submit answers
						</button>
					</div>
				</div>
			)}

			{quizSubmitted && (
				<div className={`${styles.scoreBanner} ${hasPassed ? styles.pass : styles.fail}`}>
					<div className={styles.scoreBig}>
						{quizScore} / {trainingConfig.quiz.length}
					</div>
					<p>
						{hasPassed
							? 'You passed! Your training is complete.'
							: `You need ${trainingConfig.passThreshold} correct to pass. Review the answers above, revisit any modules, and retake the quiz.`}
					</p>
					{!hasPassed && (
						<div className={styles.certActions}>
							<button className={`${styles.btn} ${styles.btnAmber}`} onClick={handleRetakeQuiz}>
								Retake quiz
							</button>
							<button className={`${styles.btn} ${styles.btnGhost}`} onClick={handleReviewModules}>
								Review modules
							</button>
						</div>
					)}
				</div>
			)}
			</div>
		</div>
	);
};

export default Training;
